import { Form } from "antd";
import Router from "next/router";
import { JIRA_OX_APP_PROJECT_ID } from "../../../../../config/constants";
import { useCreateJiraIssueMutation } from "../../../../../lib/api/endpoints/Jira/issuesEndpoints";
import { useGetUserProfileQuery } from "../../../../../lib/api/endpoints/settings/settingsEndpoints";
import { JiraIssueRequestType } from "../../../../../lib/types/jira-api";
import { TBUG_IMPACT } from "../../../../../lib/types/shared/enums";
import { GStateAction } from "../../../../../lib/types/shared/reactInherited";
import v3HandleAPIRequests from "../../../../../utils/v3.handleAPIRequests";

interface ICreateIssueFormFields {
  issuetype: string;
  summary: string;
  description?: string;
  actionBeforeBug?: string;
  businessImpact?: TBUG_IMPACT;
  personalImpact?: TBUG_IMPACT;
}

function removeTraceIdFromUrl() {
  const { traceId, ...rest } = Router.query;
  void traceId;
  Router.replace({ pathname: Router.pathname, query: rest }, undefined, {
    shallow: true
  });
}

export const useCreateJiraIssue = (setOpen: GStateAction<boolean>) => {
  const [createJiraIssue, res] = useCreateJiraIssueMutation();
  const [form] = Form.useForm();
  const traceId = (Router.query.traceId as string) || undefined;

  const onClose = () => {
    setOpen(false);
    if (Router.query.traceId) removeTraceIdFromUrl();
  };
  const currentPage = window?.location?.href || "";
  const { data } = useGetUserProfileQuery();
  const user = data?.payload;

  const onFinish = (values: ICreateIssueFormFields) => {
    const jiraIssueBody: JiraIssueRequestType = {
      fields: {
        project: { id: JIRA_OX_APP_PROJECT_ID },
        issuetype: { id: values.issuetype },
        summary: "PORTAL(AUTO): " + values?.summary?.trim(),
        description: {
          version: 1,
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Description:",
                  marks: [{ type: "strong" }]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: values.description || ""
                }
              ]
            },
            { type: "paragraph", content: [] },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Action before the issue:",
                  marks: [{ type: "strong" }]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: values.actionBeforeBug || ""
                }
              ]
            },
            { type: "paragraph", content: [] },
            { type: "rule" },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Page: ",
                  marks: [{ type: "strong" }]
                },
                {
                  type: "text",
                  text: "Bug occurred here",
                  marks: [
                    {
                      type: "link",
                      attrs: { href: currentPage }
                    },
                    { type: "strong" }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Business Impact: ",
                  marks: [{ type: "strong" }]
                },
                {
                  type: "text",
                  text: values.businessImpact || "",
                  marks: [{ type: "code" }]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Personal Impact: ",
                  marks: [{ type: "strong" }]
                },
                {
                  type: "text",
                  text: values.personalImpact || "",
                  marks: [{ type: "code" }]
                }
              ]
            },
            ...(traceId
              ? [
                  {
                    type: "paragraph",
                    content: [
                      {
                        type: "text",
                        text: "Trace ID: ",
                        marks: [{ type: "strong" }]
                      },
                      {
                        type: "text",
                        text: traceId,
                        marks: [
                          {
                            type: "link",
                            attrs: {
                              href: `${process.env.NEXT_PUBLIC_OX_TRACE_BASE_URL}/${traceId}`
                            }
                          },
                          { type: "strong" }
                        ]
                      }
                    ]
                  } as any
                ]
              : []),
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Reported by ",
                  marks: [
                    { type: "em" },
                    { type: "textColor", attrs: { color: "#97a0af" } }
                  ]
                },
                {
                  type: "text",
                  text: user?.names || "Unkown user",
                  marks: [
                    { type: "em" },
                    { type: "textColor", attrs: { color: "#97a0af" } },
                    { type: "underline" }
                  ]
                }
              ]
            }
          ]
        },
        labels: [],
        issuerestriction: { projectrole: [] },
        customfield_10021: []
      },
      update: {}
    };

    v3HandleAPIRequests({
      request: createJiraIssue,
      ...jiraIssueBody,
      onSuccess: () => {
        form.resetFields();
        if (Router.query.traceId) removeTraceIdFromUrl();
        onClose();
      },
      logSuccess: true
    });
  };

  return {
    ...res,
    form,
    onFinish,
    onClose
  };
};
